import { useCallback } from 'react';
import billingService, { buildManagerHostedCheckoutUrls } from '@/services/billingService';
import { ROUTES } from '@/core/constants/routes';

/**
 * Shared rev-share / subscription e-sign + billing API calls for manager billing flows.
 * Used by PostLoginAgreementModal (T&C gate) and AuthContext (Settings → revenue share).
 */
export function useManagerBillingSignatures() {
  const resolveContentHash = useCallback(async (signatureData) => {
    if (signatureData.pdfContentHash) {
      return signatureData.pdfContentHash;
    }
    throw new Error('Agreement PDF hash is unavailable. Please reload and try again.');
  }, []);

  const selectRevShareBilling = useCallback(async ({ firmId, userId, signatureData }) => {
    const contentHash = await resolveContentHash(signatureData);
    return billingService.selectBillingModel({
      firm_id: firmId,
      user_id: userId,
      billing_model: 'rev_share',
      signature_data: {
        signature_name: signatureData.fullName,
        signature_title: signatureData.title || '',
        signature_title_display: signatureData.titleDisplay || signatureData.title || '',
        signature_date: signatureData.timestamp,
        ip_address: signatureData.ipAddress,
        user_agent: signatureData.userAgent,
        agreement_version: signatureData.agreementVersion,
        content_hash: contentHash,
        draft_s3_key: signatureData.draftS3Key || undefined,
      },
    });
  }, [resolveContentHash]);

  const recordSubscriptionAndCheckout = useCallback(
    async ({ firmId, userId, signatureData, plan = 'annual', returnUrl }) => {
      const contentHash = await resolveContentHash(signatureData);

      await billingService.recordSubscriptionAgreement({
        firm_id: firmId,
        user_id: userId,
        signature_data: {
          signature_name: signatureData.fullName,
          signature_title: signatureData.title || '',
          signature_title_display: signatureData.titleDisplay || signatureData.title || '',
          signature_date: signatureData.timestamp,
          ip_address: signatureData.ipAddress,
          agreement_version: signatureData.agreementVersion,
          content_hash: contentHash,
          user_agent: signatureData.userAgent,
        },
      });

      const checkoutUrls = buildManagerHostedCheckoutUrls(
        returnUrl || `${window.location.origin}${ROUTES.MANAGER_DASHBOARD}`
      );
      const { url } = await billingService.createCheckoutSession({
        plan,
        ...checkoutUrls,
      });
      window.location.href = url;
    },
    [resolveContentHash]
  );

  return {
    selectRevShareBilling,
    recordSubscriptionAndCheckout,
    resolveContentHash,
  };
}

export default useManagerBillingSignatures;
